import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  Form,
  FormOptions,
  Section,
  useNotify
} from '@globaleris/ui.library'
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core'
import React, { useState } from 'react'
import { getCustomerDomain } from '../../../redux'
import { DataTableErrorHandler } from '../../errors/scopes/dataTable/dataTableError'
import { useTranslation } from 'react-i18next'
import { EnumSubTipoComunicacion, EnumTipoComunicacion } from '../../../api'
import { enumToSelectOptionArray } from '../../../helpers'
import { styles } from './comunicationExternalsTable.styles'
import { GlobalNotification } from '../../../model'
import { Pageview } from '@material-ui/icons'
import { useHttpError } from '../../../hooks'
import {
  ComunicationMailModel,
  ComunicationModel
} from '../../../api/Argentia/model/comunication/comunications'
import { EmptyContent } from '../../../pages/clients/file/components/shared/emptyCard'

export interface ComunicationExternalsTableProps {
  externals?: ComunicationModel[]
  mailingList?: ComunicationMailModel[]
  isFetchingMailingList: boolean
  onNotificationChange: (notificacion: GlobalNotification | undefined) => void
  onSelectMailingList: (id: number) => void
  onRefreshExternals?: () => void
  cardMode?: boolean
}

export const ComunicationExternalsTable: React.FC<ComunicationExternalsTableProps> = ({
  externals,
  mailingList,
  isFetchingMailingList,
  onSelectMailingList,
  onRefreshExternals,
  cardMode = false
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'comunicationExternals', 'enums'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info } = useNotify()
  const { showError, clearError, errorProps } = useHttpError()

  const [selectedMail, setSelectedMail] = useState<ComunicationModel | null>(null)
  const [showFormDetail, setShowFormDetail] = useState<boolean>(false)

  const dataTableColumns: DataTableColumn[] = [
    {
      name: 'date',
      label: t('comunicationExternals:dataTable.columns.dischargeDate'),
      type: DataType.DATE
    },
    {
      name: 'comunicationType',
      label: t('comunicationExternals:dataTable.columns.receiverType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoComunicacion, 'enums:EnumTipoComunicacion'),
      display: !cardMode
    },
    {
      name: 'comunicationSubType',
      label: t('comunicationExternals:dataTable.columns.comunicationType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumSubTipoComunicacion,
        'enums:EnumSubTipoComunicacion'
      )
    },
    {
      name: 'masterComunicationDesc',
      label: t('comunicationExternals:dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'subjectDesc',
      label: t('comunicationExternals:dataTable.columns.receiver'),
      type: DataType.TEXT
    }
    // {
    //   name: 'state',
    //   label: t('comunicationSends:dataTable.columns.state'),
    //   type: DataType.SELECT,
    //   selectOptions: enumToSelectOptionArray(EnumDispatchState, 'enums:EnumDispatchState')
    // }
  ]

  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: !cardMode,
    elevation: 0,
    filterType: !cardMode ? FilterType.ADVANCED : undefined,
    download: !cardMode,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    selectableRows: false,
    customInsertEditModal: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('comunicationExternals:dataTable.analytics')
    },
    rowCustomActions: [
      {
        icon: <Pageview fontSize="small" />,
        tooltip: t('comunicationExternals:dataTable.tooltips.view'),
        onClick: async (row: ComunicationModel) => {
          row.id && onSelectMailingList(row.id)
          setSelectedMail(row)
          setShowFormDetail(true)
        }
        //userPermission: UserPermission.READ_ONLY
      }
    ]
  }

  const formSections: Section[] = [
    {
      inputs: [
        {
          name: 'from',
          label: t('comunicationExternals:forms.detail.inputs.transmitter'),
          type: DataType.TEXT,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'date',
          label: t('comunicationExternals:forms.detail.inputs.dischargeDate'),
          type: DataType.DATE,
          readOnly: true,
          inputSize: 6
        },
        {
          name: 'to',
          label: t('comunicationExternals:forms.detail.inputs.receivers'),
          type: DataType.TEXT,
          readOnly: true,
          inputSize: 12
        },
        {
          name: 'attachment',
          label: t('comunicationExternals:forms.detail.inputs.attached'),
          type: DataType.TEXT,
          readOnly: true,
          inputSize: 12
        },
        {
          name: 'mailBody',
          label: t('comunicationExternals:forms.detail.inputs.body'),
          type: DataType.RICH_TEXT,
          readOnly: true,
          inputSize: 12
        }
      ]
    }
  ]

  const formOptions: FormOptions = {
    elevation: 0,
    columns: 1,
    analytics: {
      customer: getCustomerDomain(),
      view: t('comunicationExternals:forms.mainActions.back.inputs.attached')
    }
  }

  return (
    <>
      <DataTable
        data={externals}
        columns={dataTableColumns}
        options={dataTableOptions}
        info={DataTableErrorHandler(errorProps)}
        //userPermissions={getResourceUserPermissions(Resources.ComunicationsCenterExternals)}
      />
      <Dialog maxWidth={'md'} disableBackdropClick disableEscapeKeyDown open={showFormDetail}>
        <DialogContent>
          {isFetchingMailingList || (mailingList && mailingList.length !== 0) ? (
            <Form
              title={t('comunicationExternals:forms.detail.title')}
              sections={formSections}
              data={mailingList}
              options={formOptions}
            />
          ) : (
            <Box>
              <Typography variant="h2">Detalle de comunicación</Typography>
              <EmptyContent>
                {t('clientFile:components.externalsCard.emptyCard.title')}
              </EmptyContent>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" color="primary" onClick={() => setShowFormDetail(false)}>
            {t('comunicationExternals:forms.mainActions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
